<template>
  <div class="file-upload-warp">
    <div class="file-label-warp">
      <p class="form-label" v-if="label">
        <i v-if="required">*</i>{{ label }}<span v-if="labelTip">（{{ labelTip }}）</span>
      </p>
      <p class="file-btn" v-if="templateUrl">
        <i class="el-icon-download"></i>
        <a :href="templateUrl" :download="templateName || '模板文件'">下载{{ templateName || '模板' }}</a>
      </p>
    </div>
    <div class="upload-file">
      <el-upload
        :action="uploadUrl"
        class="upload-file-input"
        :multiple="multiple"
        :on-success="handleSuccess"
        :on-error="handleError"
        :before-upload="beforeUpload"
        :on-change="handleFileChange"
        :on-remove="handleRemove"
        :file-list="fileList"
        :with-credentials="withCredentials"
        :auto-upload="autoUpload"
        :on-exceed="handleExceed"
        :limit="limit"
        :drag="drag"
        :accept="accept"
        :disabled="disabled">
        <i class="el-icon-plus" v-if="!disabled"></i>
        <div class="el-upload__text">{{ uploadText || '点击或拖拽文件到此处上传' }}</div>
        <div class="el-upload__tip">{{ acceptTip }}</div>
      </el-upload>
    </div>
  </div>
</template>

<script>
import { Upload, Message } from "element-ui";
import { serverCourseUrl } from "@/api/api";

export default {
  name: 'FileUpload',
  components: {
    'el-upload': Upload
  },
  props: {
    // 上传地址，默认使用系统的资源上传接口
    uploadUrl: {
      type: String,
      default: () => `${serverCourseUrl}v1/resource/upload`
    },
    // 是否携带凭证
    withCredentials: {
      type: Boolean,
      default: true
    },
    multiple: {
      type: Boolean,
      default: false
    },
    // 是否自动上传
    autoUpload: {
      type: Boolean,
      default: false
    },
    // 最大上传数量
    limit: {
      type: Number,
      default: 1
    },
    // 是否拖拽上传
    drag: {
      type: Boolean,
      default: true
    },
    // 接受的文件类型
    accept: {
      type: String,
      default: '.xls,.xlsx'
    },
    // 接受的文件类型提示
    acceptTip: {
      type: String,
      default: '支持扩展名：xls，xlsx'
    },
    // 最大文件大小（MB）
    maxSize: {
      type: Number,
      default: 10
    },
    // 标签文本
    label: {
      type: String,
      default: ''
    },
    // 标签提示
    labelTip: {
      type: String,
      default: ''
    },
    // 是否必填
    required: {
      type: Boolean,
      default: false
    },
    // 模板下载链接
    templateUrl: {
      type: String,
      default: ''
    },
    // 模板名称
    templateName: {
      type: String,
      default: ''
    },
    // 上传区域提示文本
    uploadText: {
      type: String,
      default: ''
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    },
    // 超出限制时的提示
    exceedMessage: {
      type: String,
      default: ''
    },
    // 自定义文件验证函数，返回 { valid: boolean, message: string }
    customValidator: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      fileList: [],
      selectedFile: null // 存储选中的文件对象
    };
  },
  methods: {
    // 超出数量限制
    handleExceed(files, fileList) {
      const message = this.exceedMessage || `一次最多只能上传${this.limit}个文件,请删除之前的文件再上传~`;
      Message.error(message);
    },

    // 上传前验证
    beforeUpload(file) {
      const isLtMaxSize = file.size / 1024 / 1024 < this.maxSize;

      if (!isLtMaxSize) {
        Message.error(`上传文件大小不能超过 ${this.maxSize}MB!`);
        return false;
      }

      // 如果有自定义验证器，执行自定义验证
      if (this.customValidator) {
        const result = this.customValidator(file);
        if (!result.valid) {
          Message.error(result.message || '文件验证失败');
          return false;
        }
      }

      return true;
    },

    // 文件选择变化处理
    handleFileChange(file, fileList) {
      // 验证文件 - 兼容不同版本的Element UI
      const rawFile = file.raw || file;

      if (!rawFile) {
        return;
      }

      // 验证文件大小
      const isLtMaxSize = rawFile.size / 1024 / 1024 < this.maxSize;

      if (!isLtMaxSize) {
        Message.error(`上传文件大小不能超过 ${this.maxSize}MB!`);
        this.selectedFile = null;
        this.fileList = [];
        this.$emit('error', new Error(`上传文件大小不能超过 ${this.maxSize}MB!`));
        return;
      }

      // 如果有自定义验证器，执行自定义验证
      if (this.customValidator) {
        const result = this.customValidator(rawFile);
        if (!result.valid) {
          Message.error(result.message || '文件验证失败');
          this.selectedFile = null;
          this.fileList = [];
          this.$emit('error', new Error(result.message || '文件验证失败'));
          return;
        }
      }

      // 确保存储的是真正的File对象
      if (!(rawFile instanceof File)) {
        Message.error('文件格式不正确，请重新选择文件');
        this.selectedFile = null;
        this.fileList = [];
        this.$emit('error', new Error('文件格式不正确'));
        return;
      }

      // 存储文件对象
      this.selectedFile = rawFile;
      this.fileList = fileList;

      // 触发文件变化事件
      this.$emit('change', rawFile, fileList);
    },

    // 上传成功回调
    handleSuccess(response, file, fileList) {
      this.fileList = fileList;
      this.$emit('success', response, file, fileList);
    },

    // 上传失败回调
    handleError(error, file, fileList) {
      this.fileList = fileList;
      this.$emit('error', error, file, fileList);
    },

    // 移除文件
    handleRemove(file, fileList) {
      this.selectedFile = null;
      this.fileList = fileList;
      this.$emit('remove', file, fileList);
    },

    // 获取选中的文件
    getFile() {
      return this.selectedFile;
    },

    // 清空文件
    clearFiles() {
      this.selectedFile = null;
      this.fileList = [];
    },

    // 设置文件列表
    setFileList(fileList) {
      this.fileList = fileList;
    }
  }
};
</script>

<style scoped lang="less">
.file-upload-warp {
  width: 100%;

  .file-label-warp {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;

    .form-label {
      text-align: left;
      font-size: 18px;
      color: #1D2129;
      display: flex;
      align-items: center;
      font-weight: 500;

      span {
        color: #4E5969;
        font-size: 13px;
        font-weight: 400;
      }

      i {
        color: #E60012;
        margin-right: 4px;
      }
    }

    .file-btn {
      display: flex;
      align-items: center;
      font-size: 16px;
      font-weight: 500;

      i {
        color: #3491FA;
      }

      a {
        color: #3491FA;
        margin-left: 8px;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  .upload-file {
    width: 100%;
    height: 184px;
    position: relative;
    .upload-file-input {
      height: 100%;
      width: 100%;
    }
    .el-icon-plus {
      margin-bottom: 20px;
      font-size: 14px;
      color: #4E5969;
    }
    .el-upload__text {
      font-size: 14px;
      color: #1D2129;
    }
    .el-upload__tip {
      color: #86909C;
      font-size: 12px;
      margin-top: 4px;
    }
  }
}

// 全局样式覆盖（与原文件保持一致）
/deep/ .el-upload {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/deep/ .el-upload-list {
  position: absolute;
  left: 0;
  bottom: -40px;
}

/deep/ .el-upload-dragger {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  background: #F7F6F5;
  border-color: #E5E6EB;
  &:hover {
    border-color: #3491FA;
  }
}

</style>
